/**
 * UseMyAssignmentsQuery Hook
 *
 * Fetches the list of training assignments for the currently logged-in user.
 * Used on the /my-training page to display user's training dashboard.
 *
 * Pattern: TanStack Query hook with proper cache management
 */

import {useQuery} from '@tanstack/react-query';
import {useApiToken} from '@/matic/hooks/useApiClient';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {getTrainingAssignmentsApi} from '@/matic/features/training/api/training-assignments-api';
import type {MyAssignmentFilters} from '../types';

/** Query key factory for my assignments queries */
export const myAssignmentKeys = {
  all: ['myAssignments'] as const,
  lists: () => [...myAssignmentKeys.all, 'list'] as const,
  list: (tenantId: string, filters: MyAssignmentFilters) =>
    [...myAssignmentKeys.lists(), tenantId, JSON.stringify(filters)] as const,
};

/**
 * Fetch user's training assignments
 *
 * Returns a paginated list of training assignments for the current user,
 * filtered by status if specified.
 *
 * @param filters - Optional filters (status, search, pagination)
 * @returns Query result with MyAssignmentList
 */
export function useMyAssignmentsQuery(filters: MyAssignmentFilters = {}) {
  const token = useApiToken();
  const tenantId = useRequiredTenantId();
  const api = getTrainingAssignmentsApi(token);

  return useQuery({
    queryKey: myAssignmentKeys.list(tenantId, filters),
    queryFn: async () =>
      api.getMyAssignments({
        status: filters.status,
      }),
    enabled: Boolean(token),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
}
